import React, { useEffect, useState } from 'react';
import {
  Box, Card, Typography, Chip, Button, Divider, CircularProgress,
  LinearProgress, Tooltip, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchBasket } from '../../store/optionsSlice';
import { SignalData, ScoreComponent, BasketData, SafetyGate } from '../../store/optionsSlice';
import ExecutionModal from './ExecutionModal';
import { useAppTheme } from '../../hooks/useAppTheme';

interface Props {
  signal: SignalData;
  basketId?: number;
  onOpenLab?: (symbol: string) => void;
}

const SignalCard: React.FC<Props> = ({ signal, basketId, onOpenLab }) => {
  const { C } = useAppTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { activeBasket, safetyGates } = useSelector((s: RootState) => s.options);
  const [showModal, setShowModal] = useState(false);
  const [loadingBasket, setLoadingBasket] = useState(false);

  const scoreColor = (s: number) => s >= 85 ? C.bull : s >= 70 ? C.warn : C.bear;

  // Signal age timer (Phase 4)
  const [signalAge, setSignalAge] = useState(0);
  useEffect(() => {
    if (!signal?.createdAt) return;
    const update = () => {
      const ageMs = Date.now() - new Date(signal.createdAt).getTime();
      setSignalAge(Math.floor(ageMs / 1000));
    };
    update();
    const interval = setInterval(update, 10000);
    return () => clearInterval(interval);
  }, [signal?.createdAt]);
  const signalAgeMinutes = Math.floor(signalAge / 60);
  const signalAgeColor = signalAgeMinutes < 5 ? C.bull : signalAgeMinutes < 15 ? C.warn : C.bear;

  useEffect(() => {
    if (basketId && !activeBasket) {
      setLoadingBasket(true);
      dispatch(fetchBasket(basketId)).finally(() => setLoadingBasket(false));
    }
  }, [basketId, dispatch]);

  const score = Number(signal.score);
  const regimeTag = signal.regimeTag || 'REVIEW';
  const components: ScoreComponent[] = signal.triggerConditions?.components || [];
  const topComponents = [...components].sort((a, b) => b.weight - a.weight).slice(0, 8);

  return (
    <>
      <Card sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>

          {/* ── Left Panel: Signal Quality ───────────────────────────────── */}
          <Box sx={{ flex: '1 1 280px', p: 2.5 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Chip
                label={signal.direction}
                size="small"
                sx={{
                  bgcolor: signal.direction === 'LONG' ? C.bullMuted : C.bearMuted,
                  color: signal.direction === 'LONG' ? C.bull : C.bear,
                  fontWeight: 800, fontSize: '0.8rem', letterSpacing: 1
                }}
              />
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {/* Try to derive symbol from underlying — not available directly, show strategy */}
                {signal.strategyRecommendation || 'Options Signal'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
                {new Date(signal.createdAt).toLocaleTimeString('en-IN')}
              </Typography>
              <Chip size="small"
                label={signalAgeMinutes > 30 ? 'EXPIRED' : signalAgeMinutes < 1 ? 'Just now' : `${signalAgeMinutes}m ago`}
                sx={{ bgcolor: `${signalAgeColor}22`, color: signalAgeColor, fontWeight: 700, fontSize: '0.65rem' }}
              />
            </Box>

            {/* Confluence Score */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
                  CONFLUENCE SCORE
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 900, color: scoreColor(score), lineHeight: 1 }}>
                  {score.toFixed(0)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(score, 100)}
                sx={{
                  height: 8, borderRadius: 4,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': { bgcolor: scoreColor(score), borderRadius: 4 }
                }}
              />
              <Chip
                size="small" label={regimeTag}
                sx={{
                  mt: 1,
                  bgcolor: regimeTag === 'AUTO-APPROVE' ? C.bullMuted
                    : regimeTag === 'REVIEW' ? C.warnMuted : C.bearMuted,
                  color: regimeTag === 'AUTO-APPROVE' ? C.bull
                    : regimeTag === 'REVIEW' ? C.warn : C.bear,
                  fontWeight: 800, fontSize: '0.72rem', letterSpacing: 0.5
                }}
              />
            </Box>

            {/* Component checklist */}
            <Box sx={{ mb: 2 }}>
              {topComponents.map((c) => {
                const isNeutral = c.pass && c.score === 50 && (c.reason.toLowerCase().includes('insufficient') || c.reason.toLowerCase().includes('neutral') || c.reason.toLowerCase().includes('unavailable'));
                return (
                  <Box key={c.name} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                    {isNeutral
                      ? <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: C.dim, mt: 0.1, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography sx={{ color: C.onSolid, fontSize: 10, fontWeight: 700, lineHeight: 1 }}>—</Typography>
                        </Box>
                      : c.pass
                        ? <CheckCircleOutlineIcon sx={{ fontSize: 16, color: C.bull, mt: 0.1, flexShrink: 0 }} />
                        : <CancelOutlinedIcon sx={{ fontSize: 16, color: C.bear, mt: 0.1, flexShrink: 0 }} />}
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: isNeutral ? C.dim : 'text.primary' }}>{c.name}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {(c.weight * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: isNeutral ? C.dim : 'text.secondary', fontSize: '0.65rem' }}>
                        {c.reason}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Strategy pill */}
            <Chip
              label={`Strategy: ${signal.strategyRecommendation || 'TBD'}`}
              size="small"
              sx={{ bgcolor: C.neutralMuted, color: C.neutral, fontWeight: 700 }}
            />

            {/* Score Gap Explanation */}
            {(signal.triggerConditions as any)?.scoreGapExplanation && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25, fontSize: '0.62rem' }}>
                  WHY NOT 85?
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                  {(signal.triggerConditions as any).scoreGapExplanation}
                </Typography>
              </Box>
            )}

            {/* Futures-Options Agreement Indicator */}
            {(() => {
              const tc = signal.triggerConditions as any;
              const agreement = tc?.futuresOptionsAgreement;
              if (!agreement || agreement === 'UNKNOWN') return null;
              const isConflict = agreement === 'CONFLICT';
              return (
                <Box sx={{
                  mt: 1, p: 1, borderRadius: 1,
                  bgcolor: isConflict ? C.warnMuted : C.bullMuted,
                  border: `1px solid ${isConflict ? C.warnBorder : C.bullBorder}`
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: isConflict ? C.warn : C.bull, letterSpacing: 0.3 }}>
                    {isConflict ? 'FUTURES & OPTIONS CONFLICTED' : 'FUTURES & OPTIONS AGREE'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mt: 0.25 }}>
                    {tc?.foExplanation || ''}
                  </Typography>
                  {isConflict && (
                    <Typography variant="caption" sx={{ color: C.warn, fontSize: '0.65rem', fontWeight: 700, display: 'block', mt: 0.25 }}>
                      Conflicted signals: consider reducing position size or skipping.
                    </Typography>
                  )}
                </Box>
              );
            })()}

            {/* Tradability Status */}
            {(() => {
              const tc = signal.triggerConditions as any;
              const issues: string[] = [];
              if (tc?.isStale) issues.push('Data is stale — refresh required');
              if (signalAgeMinutes > 15) issues.push(`Signal is ${signalAgeMinutes} min old — parameters may be outdated`);
              if (!activeBasket && !loadingBasket) issues.push('No basket available — option chain data may be incomplete');
              const tradable = issues.length === 0;
              return (
                <Box sx={{
                  mt: 1, p: 1, borderRadius: 1,
                  bgcolor: tradable ? C.bullMuted : C.bearMuted,
                  border: `1px solid ${tradable ? C.bullBorder : C.bearBorder}`
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: tradable ? C.bull : C.bear, letterSpacing: 0.3 }}>
                    {tradable ? '● TRADABLE' : '○ NOT TRADABLE'}
                  </Typography>
                  {issues.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      {issues.map((issue, i) => (
                        <Typography key={i} variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.62rem' }}>
                          • {issue}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })()}
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* ── Right Panel: Execution Hub ───────────────────────────────── */}
          <Box sx={{ flex: '1 1 280px', p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, letterSpacing: 0.5 }}>
              EXECUTION HUB
            </Typography>

            {loadingBasket ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Preparing strategy basket…</Typography>
              </Box>
            ) : activeBasket ? (
              <BasketPreview basket={activeBasket} gates={safetyGates} />
            ) : signal.basketId ? (
              <Box sx={{ p: 1, bgcolor: C.warnMuted, borderRadius: 1, border: `1px solid ${C.warnBorder}`, mb: 1 }}>
                <Typography variant="caption" sx={{ color: C.warn, fontWeight: 700, display: 'block', mb: 0.25 }}>
                  ⏳ Loading basket details…
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Payoff graph, premium required, ROI and win probability will appear here.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 1, bgcolor: C.bearMuted, borderRadius: 1, border: `1px solid ${C.bearBorder}`, mb: 1 }}>
                <Typography variant="caption" sx={{ color: C.bear, fontWeight: 700, display: 'block', mb: 0.25 }}>
                  Basket unavailable
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Option chain data may be stale — no strategy could be built. Refresh data and run Analyze again.
                </Typography>
              </Box>
            )}

            {/* Stale signal warning */}
            {signalAgeMinutes > 15 && signalAgeMinutes <= 30 && (
              <Box sx={{ p: 1, bgcolor: C.warnMuted, borderRadius: 1, border: `1px solid ${C.warnBorder}`, mb: 1 }}>
                <Typography variant="caption" sx={{ color: C.warn, fontWeight: 700, display: 'block' }}>
                  BASKET PARAMETERS MAY BE STALE
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Signal is {signalAgeMinutes} min old. Spot may have moved significantly. Refresh data and re-analyze before placing.
                </Typography>
              </Box>
            )}
            {signalAgeMinutes > 30 && (
              <Box sx={{ p: 1, bgcolor: C.bearMuted, borderRadius: 1, border: `1px solid ${C.bearBorder}`, mb: 1 }}>
                <Typography variant="caption" sx={{ color: C.bear, fontWeight: 700, display: 'block' }}>
                  SIGNAL EXPIRED — Re-analyze Required
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Signal is over 30 minutes old. Strike prices and premiums are no longer valid.
                </Typography>
              </Box>
            )}

            {/* Action buttons — 4-state model */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
              {(() => {
                const s = Number(signal.score);
                const isExpired = signalAgeMinutes > 30;
                const allCriticalPass = safetyGates.length === 0 || !safetyGates.some(g => g.blocking && !g.pass);

                // State 4: Score < 55 OR hard gate failure OR expired
                if (s < 55 || (safetyGates.length > 0 && !allCriticalPass)) {
                  return (
                    <Box sx={{ width: '100%' }}>
                      <Button variant="contained" fullWidth disabled
                        sx={{ fontWeight: 800, '&.Mui-disabled': { bgcolor: C.bearMuted, color: C.bear } }}>
                        Signal Rejected
                      </Button>
                      <Typography variant="caption" sx={{ color: C.bear, display: 'block', textAlign: 'center', mt: 0.5 }}>
                        Score too low or safety gate failure — view component details above
                      </Typography>
                    </Box>
                  );
                }

                // State 3: Score 55-64 → Override & Accept Risk (dark orange)
                if (s < 65) {
                  return (
                    <Tooltip title="This is a weak signal. You will need to confirm you understand the risk before placing." arrow>
                      <span style={{ width: '100%' }}>
                        <Button variant="contained" fullWidth
                          disabled={!activeBasket || signal.status === 'EXECUTED' || isExpired}
                          onClick={() => setShowModal(true)}
                          sx={{ bgcolor: C.warn, color: '#000', fontWeight: 800, '&:hover': { filter: 'brightness(0.9)' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}>
                          Override & Accept Risk
                        </Button>
                      </span>
                    </Tooltip>
                  );
                }

                // State 2: Score 65-84 (REVIEW) → Review & Approve (amber)
                if (s < 85) {
                  return (
                    <Button variant="contained" fullWidth
                      disabled={!activeBasket || signal.status === 'EXECUTED' || isExpired}
                      onClick={() => setShowModal(true)}
                      sx={{ bgcolor: C.warn, color: '#000', fontWeight: 800, '&:hover': { filter: 'brightness(0.9)' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}>
                      Review & Approve
                    </Button>
                  );
                }

                // State 1: Score >= 85 → Approve & Place (green)
                return (
                  <Button variant="contained" fullWidth
                    disabled={!activeBasket || signal.status === 'EXECUTED' || isExpired}
                    onClick={() => setShowModal(true)}
                    sx={{ bgcolor: C.bull, color: C.onSolid, fontWeight: 800, '&:hover': { filter: 'brightness(0.9)' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}>
                    Approve & Place
                  </Button>
                );
              })()}

              <Button variant="outlined" fullWidth color="error"
                onClick={() => {/* handled by parent or modal */}}>
                Reject
              </Button>
              <Button variant="text" fullWidth startIcon={<ScienceIcon />}
                onClick={() => onOpenLab?.('')} sx={{ color: C.neutral }}>
                Open Lab
              </Button>
            </Box>
          </Box>
        </Box>
      </Card>

      {showModal && activeBasket && (
        <ExecutionModal
          signal={signal}
          basket={activeBasket}
          gates={safetyGates}
          open={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

// ── Basket preview in execution hub ────────────────────────────────────────

const BasketPreview: React.FC<{ basket: BasketData; gates: SafetyGate[] }> = ({ basket, gates }) => {
  const { C } = useAppTheme();
  const blockingFail = gates.filter(g => g.blocking && !g.pass);

  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.3, mb: 0.5, display: 'block' }}>
        {basket.strategy}
      </Typography>

      {/* Leg table */}
      {basket.legs.slice(0, 4).map((leg, i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Chip
              size="small" label={leg.action}
              sx={{
                bgcolor: leg.action === 'BUY' ? C.bullMuted : C.bearMuted,
                color: leg.action === 'BUY' ? C.bull : C.bear,
                fontSize: '0.65rem', fontWeight: 700, height: 18
              }}
            />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {leg.strike} {leg.optionType}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            ₹{Number(leg.ltp || 0).toFixed(1)}
          </Typography>
        </Box>
      ))}

      <Divider sx={{ my: 1 }} />

      {/* Risk metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
        <RiskMetric label="Net Premium" value={`₹${Number(basket.netPremium || 0).toFixed(0)}`} />
        <RiskMetric label="Max Profit" value={`₹${Number(basket.maxProfit || 0).toFixed(0)}`} color={C.bull} />
        <RiskMetric label="Max Loss" value={`₹${Math.abs(Number(basket.maxLoss || 0)).toFixed(0)}`} color={C.bear} />
        <RiskMetric label="Margin Req." value={`₹${Number(basket.marginEstimate || 0).toFixed(0)}`} />
        {(() => {
          const pd = basket.payoffData as any;
          const premReq = Array.isArray(pd?.premiumRequired) ? pd.premiumRequired[0] : pd?.premiumRequired;
          const roi = Array.isArray(pd?.roi) ? pd.roi[0] : pd?.roi;
          const winProb = Array.isArray(pd?.winProbability) ? pd.winProbability[0] : pd?.winProbability;
          return (
            <>
              {Number(premReq) > 0 && (
                <RiskMetric
                  label="Premium Req."
                  value={`₹${Number(premReq).toFixed(0)}`}
                  color={Number(premReq) > Number(basket.marginEstimate || 0) ? C.bear : undefined}
                />
              )}
              {roi != null && (
                <RiskMetric
                  label="ROI (est.)"
                  value={`${Number(roi).toFixed(1)}%`}
                  color={Number(roi) > 0 ? C.bull : C.bear}
                />
              )}
              {winProb != null && (
                <RiskMetric
                  label="Win Prob."
                  value={`${Number(winProb).toFixed(0)}%`}
                  color={Number(winProb) >= 50 ? C.bull : C.warn}
                />
              )}
            </>
          );
        })()}
      </Box>

      {/* Safety gate summary — informational only, no hard block */}
      {blockingFail.length > 0 && (
        <Box sx={{ mt: 1, p: 1, bgcolor: C.warnMuted, borderRadius: 1, border: `1px solid ${C.warnBorder}` }}>
          <Typography variant="caption" sx={{ color: C.warn, fontWeight: 700 }}>
            ⚠ {blockingFail.length} gate(s) to review: {blockingFail.map(g => g.name).join(', ')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
            You can still proceed — review the risk before placing.
          </Typography>
        </Box>
      )}

      {/* Position Greeks (estimated) */}
      {(() => {
        const pd = basket.payoffData as any;
        const theta = Array.isArray(pd?.theta) ? pd.theta[0] : pd?.theta;
        const delta = Array.isArray(pd?.positionDelta) ? pd.positionDelta[0] : pd?.positionDelta;
        const vega = Array.isArray(pd?.vega) ? pd.vega[0] : pd?.vega;
        if (theta == null && delta == null && vega == null) return null;
        return (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.3, mb: 0.5, display: 'block' }}>
              POSITION GREEKS (est.)
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.5 }}>
              {theta != null && (
                <Tooltip title="Daily P&L if spot stays flat" arrow>
                  <Box><RiskMetric label="Theta/day" value={`₹${Number(theta).toFixed(0)}`} color={Number(theta) < 0 ? C.bear : C.bull} /></Box>
                </Tooltip>
              )}
              {delta != null && (
                <Tooltip title="P&L per 100pt move in expected direction" arrow>
                  <Box><RiskMetric label="Delta" value={Number(delta).toFixed(2)} /></Box>
                </Tooltip>
              )}
              {vega != null && (
                <Tooltip title="P&L per 1% IV change" arrow>
                  <Box><RiskMetric label="Vega" value={`₹${Number(vega).toFixed(0)}`} /></Box>
                </Tooltip>
              )}
            </Box>
          </>
        );
      })()}

      {/* Why This Strategy */}
      {(basket.payoffData as any)?.whyThisStrategy && (
        <Accordion disableGutters elevation={0} sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: '4px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
            sx={{ minHeight: 28, '& .MuiAccordionSummary-content': { my: 0.25 } }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: C.neutral }}>Why this strategy?</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {(basket.payoffData as any).whyThisStrategy}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {/* What Can Invalidate */}
      {(basket.payoffData as any)?.whatCanInvalidate && (
        <Accordion disableGutters elevation={0} sx={{ mt: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: '4px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
            sx={{ minHeight: 28, '& .MuiAccordionSummary-content': { my: 0.25 } }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: C.bear }}>What can invalidate this trade?</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1 }}>
            {((basket.payoffData as any).whatCanInvalidate as string[]).map((item: string, i: number) => (
              <Typography key={i} variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
                {i + 1}. {item}
              </Typography>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

const RiskMetric: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem' }}>{label}</Typography>
    <Typography variant="caption" sx={{ fontWeight: 700, color: color || 'text.primary', display: 'block' }}>{value}</Typography>
  </Box>
);

export default SignalCard;
